//#region Util Imports

import * as _ from 'underscore'

//#endregion

//#region DevExtreme Imports

import DevExpress from 'devextreme'

//#endregion

//#region IMR Imports

import { IMRPopupOptions, IIMRPopup } from '@imr/controls.angularjs/components/overlay/popup'
import { IMRComponentInitializedArgs } from '@imr/controls.angularjs/components/base'

//#endregion

import { Enums } from '../../../../models/common'
import { OpDepositoryElement, OpPackage } from '../../../../models/core'

import { LocalizationHelper, TypeHelper, ToastHelper, ConvertHelper, LogHelper } from '../../../../helpers'

import { OrderService, OrderCompleteService } from '../../../../services/order'

import { ColumnModel } from '../../../api/api.models'

import { GridDataSourceType, GridOptions, GridTypes } from '../../../common/grid'

import { OrderValidationOptions, IOrderValidation } from './components/validation'
import { ICompleteOrder, CompleteOrderOptions } from './complete-order.interfaces'

export class CompleteOrderController implements ng.IComponentController, ICompleteOrder {

    //#region Consts

    private get ORDER_GRID_NAME(): string { return 'ORDERS_COMPLETED_ORDER_ORDER_GRID_NAME' }
    private get PACKAGE_CONTENT_GRID_NAME(): string { return 'ORDERS_COMPLETE_ORDER_PACKAGE_CONTENT_GRID_NAME' }

    //#endregion


    //#region Members

    public contentGridOptions: GridOptions
    public popupOptions: IMRPopupOptions
    public options: CompleteOrderOptions
    public orderGridOptions: GridOptions
    public validationOptions: OrderValidationOptions

    private incompleteContent: OpDepositoryElement[]
    private orderValidation: IOrderValidation
    private orders: OpPackage[]
    private popup: IIMRPopup
    private validateButton: DevExpress.ui.dxButton


    //#endregion 


    //#region Ctor

    constructor(
        private orderService: OrderService,
        private completeOrderService: OrderCompleteService
    ) { }

    //#endregion


    //#region ng.IComponentController Implementation

    public $onChanges(changes: { [index: string]: ng.IChangesObject<any> }): void {
        if (TypeHelper.isOptionsDefined(changes)) {
            this.initialize()
        }
    }

    //#endregion


    //#region Component

    private initialize(): void {
        this.initializeValidation()
        this.initializePopup()
            .then(() => {
                this.onInitialized()
            })
    }

    private reset(): void {
        this.orderGridOptions = null
        this.contentGridOptions = null
        this.orders = []

        this.toggleValidateButton(true)
    }

    //#endregion


    //#region Events

    private onInitialized(): void {
        if (this.options != null && _.isFunction(this.options.onInitialized)) {
            this.options.onInitialized(this)
        }
    }

    private onOrderCompleted(): void {
        if (this.options != null && _.isFunction(this.options.onOrderCompleted)) {
            this.options.onOrderCompleted()
        }
    }

    //#endregion


    //#region IOrdersCompleteOrder Implementation

    public complete(orders: OpPackage[]): void {
        this.orders = _.map(orders, (order: OpPackage) => order.clone(true, true, true, true))

        this.popup.toggleLoading(true)
        this.popup.show()

        $
            .when(
                this.initializeOrders(this.orders),
                this.initializeContent(this.orders)
            )
            .always(() => {
                this.popup.toggleLoading(false)
            })
    }

    //#endregion


    //#region Order Content

    private initializeContent(orders: OpPackage[]): JQueryPromise<void> {
        return this.completeOrderService
            .getIncompleteContent(orders)
            .then((incompleteContent: OpDepositoryElement[]) => {
                this.incompleteContent = incompleteContent

                return this.initializeContentGrid()
            })
    }
    private initializeContentGrid(): any {
        this.contentGridOptions = {
            columnsDictionary: this.completeOrderService.getContentGridColumns(true),
            dataSourceType: GridDataSourceType.Properties,
            name: this.PACKAGE_CONTENT_GRID_NAME,
            type: GridTypes.PAGINATED_SMALL,

            buttonsOptions: {
                enabled: true,
                isRefreshButonHidden: true
            },

            editing: {
                allowUpdating: true,
                mode: "cell",
            },

            scrolling: {
                mode: "virtual"
            },

            getDataSource: () => {
                return this.incompleteContent
            },

            onRowUpdating: (e: any) => {
                const editedContentItem: OpDepositoryElement = this.orderService.getContentItemFromGridObject(e.key)
                const existingItem: OpDepositoryElement = _.find(this.incompleteContent, (item: OpDepositoryElement) => item.idDepositoryElement === editedContentItem.idDepositoryElement)

                existingItem.externalSn = e.newData.externalSn

                this.toggleValidateButton(!_.some(this.incompleteContent, (contentItem: OpDepositoryElement) => !TypeHelper.isNullOrEmpty(contentItem.externalSn)))
            }
        }

        return TypeHelper.onInitializedAll(this.contentGridOptions)
    }

    private getContentToValidate(): OpDepositoryElement[] {
        return _.filter(this.incompleteContent, (contentItem: OpDepositoryElement) => {
            return !TypeHelper.isNullOrEmpty(contentItem.externalSn)
        })
    }

    //#endregion


    //#region Orders

    private initializeOrders(orders: OpPackage[]): JQueryPromise<void> {
        return this.orderService
            .getMainGridColumns()
            .then((columns: ColumnModel[]) => {
                this.orderGridOptions = {
                    type: GridTypes.PAGINATED_SMALL,
                    name: this.ORDER_GRID_NAME,
                    columnsDictionary: columns,
                    dataSource: orders,

                    buttonsOptions: {
                        enabled: true
                    },
                }

                return TypeHelper.onInitializedAll(this.orderGridOptions)
            })
    }

    private completeOrder(ordersToComplete: OpPackage[], contentToComplete: OpDepositoryElement[]): void {
        this.popup.toggleLoading(true)

        const newOrders: OpPackage[] = _.map(ordersToComplete, (order: OpPackage) => {
            const ordersIncompleteContent: OpDepositoryElement[] = _.filter(this.incompleteContent, (contentItem: OpDepositoryElement) => contentItem.idPackage === order.idPackage)
            const ordersValidatedContent: OpDepositoryElement[] = _.filter(contentToComplete, (contentItem: OpDepositoryElement) => contentItem.idPackage === order.idPackage)
            const isOrderComplete: boolean = _.size(ordersValidatedContent) === _.size(ordersIncompleteContent)
            const orderToComplete: OpPackage = order.clone()

            orderToComplete.contentList = ordersValidatedContent
            orderToComplete.idPackageStatus = isOrderComplete ? Enums.PackageStatus.OrderCompleted : Enums.PackageStatus.OrderPartiallyCompleted

            return orderToComplete
        })

        this.completeOrderService
            .completeOrders(newOrders)
            .then(() => {
                this.onOrderCompleted()
                this.popup.hide()
            })
            .fail((error: any) => {
                ToastHelper.showError(ConvertHelper.getRequestErrorMessage(error) || LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ExceptionOccuredPleaseContactAnAdministrator))
                LogHelper.warn(OrderService.LOG_PREFIX, `Unable to save orders to operator receivers.`, error)
            })
            .always(() => {
                this.popup.toggleLoading(false)
            })
    }

    private validateOrder(): void {
        this.orderValidation.validate(this.getContentToValidate())
    }

    //#endregion


    //#region Popup

    private initializePopup(): JQueryPromise<void> {
        this.popupOptions = {
            bottomToolbarOptions: {
                innerOptions: {
                    items: [{
                        location: 'after',
                        widget: 'dxButton',
                        options: {
                            disabled: true,
                            text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Validate),

                            onClick: () => {
                                this.validateOrder()
                            },

                            onInitialized: (e: any) => {
                                this.validateButton = e.component
                            }
                        }
                    }],
                }
            },

            innerOptions: {
                title: LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__ADDING, Enums.SimaxModuleName.Package),

                onHidden: () => {
                    this.reset()
                },
            },

            onInitialized: (e: IMRComponentInitializedArgs<IIMRPopup>) => {
                this.popup = e.component
            }
        }

        return TypeHelper.onInitializedAll(this.popupOptions)
    }

    private toggleValidateButton(disabled: boolean = false): void {
        if (this.validateButton != null) {
            this.validateButton.option('disabled', disabled)
        }
    }

    //#endregion


    //#region Validation

    private initializeValidation(): void {
        this.validationOptions = {
            onInitialized: (orderValidation: IOrderValidation) => {
                this.orderValidation = orderValidation
            },

            onValidated: (validatedContent: OpDepositoryElement[]) => {
                this.completeOrder(this.orders, validatedContent)
            },
        }
    }

    //#endregion
}